/**
 * Target entities of a Home Assistant action node.
 *
 * In the flow graph an action's target entities are the entity nodes wired
 * to its `target` input. The action JSON (AutomationAction.toJSON) can carry
 * the entity in three places, and some actions use more than one:
 *   - `editorMetadata.targetEntityId` (text to speech; authoritative there),
 *   - `target.entity_id` (a string or a list),
 *   - the `entity_id` parameter (a literal or a list value).
 *
 * Migration takes concrete entity ids out of the action and leaves an empty
 * marker of the same shape in the same place (`''`, `[]`, an empty literal or
 * list). Compiling fills the markers again, so the exported action keeps its
 * keys, their order and their string-or-list form.
 */

// A concrete Home Assistant entity id: `domain.object_id`, no template.
const ENTITY_ID = /^[a-z_][a-z0-9_]*\.[a-z0-9_]+$/;
// `object.<property>` is a Designer reference to the source object, not an entity.
const isEntityId = value => typeof value === 'string' && ENTITY_ID.test(value) && !value.startsWith('object.');

const parameterIndex = action => (action?.parameters ?? []).findIndex(parameter => parameter?.key === 'entity_id');

/** Entity ids held by a stored value, '' / [] when empty, or null when not concrete entities. */
function entitiesOf(value) {
    if (value === '' || (Array.isArray(value) && value.length === 0)) return [];
    if (isEntityId(value)) return [value];
    if (Array.isArray(value) && value.every(isEntityId)) return [...value];
    return null;
}

function parameterEntities(value) {
    if (value?.kind === 'literal') return entitiesOf(value.value);
    if (value?.kind === 'list') return entitiesOf(value.items ?? []);
    return null;
}

/**
 * Take the concrete target entities out of an action (JSON, not mutated).
 * @returns {{ entities: string[], action: object } | null} null when the
 *   action has no concrete target entity, or its places disagree.
 */
export function extractTargetEntities(action) {
    const places = [];
    const metadataId = action?.editorMetadata?.targetEntityId;
    if (typeof metadataId === 'string') places.push(entitiesOf(metadataId));
    if (action?.target && typeof action.target === 'object' && 'entity_id' in action.target) {
        places.push(entitiesOf(action.target.entity_id));
    }
    const index = parameterIndex(action);
    if (index >= 0) places.push(parameterEntities(action.parameters[index].value));

    if (places.length === 0 || places.some(place => place === null)) return null;
    const filled = places.filter(place => place.length > 0);
    if (filled.length === 0) return null;
    const entities = filled[0];
    if (filled.some(place => place.join('\n') !== entities.join('\n'))) return null;

    const result = structuredClone(action);
    if (typeof metadataId === 'string') result.editorMetadata.targetEntityId = '';
    if (result.target && 'entity_id' in result.target) {
        result.target.entity_id = Array.isArray(result.target.entity_id) ? [] : '';
    }
    if (index >= 0) {
        const value = result.parameters[index].value;
        result.parameters[index].value = value.kind === 'list' ? { kind: 'list', items: [] } : { kind: 'literal', value: '' };
    }
    return { entities, action: result };
}

/** True when an action holds an empty target marker left by extractTargetEntities. */
const isMarker = value => value === '' || (Array.isArray(value) && value.length === 0);

/**
 * Put target entities back into an action's markers (JSON, not mutated).
 * Without entities the markers are removed, so the export treats the action
 * as having no target entity (and the Primary Entity can apply).
 */
export function applyTargetEntities(action, entities) {
    const result = structuredClone(action);
    const ids = [...(entities ?? [])];
    const asStored = marker => (Array.isArray(marker) || ids.length > 1 ? ids : ids[0]);

    if (result.editorMetadata && result.editorMetadata.targetEntityId === '' && ids.length > 0) {
        result.editorMetadata.targetEntityId = ids[0];
    }
    if (result.target && typeof result.target === 'object' && isMarker(result.target.entity_id)) {
        if (ids.length > 0) result.target.entity_id = asStored(result.target.entity_id);
        else delete result.target.entity_id;
    } else if (ids.length > 0 && result.target?.entity_id === undefined && parameterIndex(result) < 0 &&
        result.editorMetadata?.targetEntityId === undefined) {
        // An action that never had a target entity gets one in `target`.
        result.target = { ...(result.target ?? {}), entity_id: ids.length > 1 ? ids : ids[0] };
    }
    const index = parameterIndex(result);
    if (index >= 0) {
        const value = result.parameters[index].value;
        const empty = value?.kind === 'list' ? (value.items ?? []).length === 0 : value?.kind === 'literal' && value.value === '';
        if (empty && ids.length > 0) {
            result.parameters[index].value = value.kind === 'list' || ids.length > 1
                ? { kind: 'list', items: ids }
                : { kind: 'literal', value: ids[0] };
        } else if (empty) {
            result.parameters.splice(index, 1);
        }
    }
    return result;
}
